import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import m4th from "m4th";
import { Helmet, HelmetProvider } from "react-helmet-async";
import Pendulum from "./pendulum";
import Latex from "react-latex-next";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

const M = m4th.matrix;

function Main() {
  let translated = false;
  const [showIteration, setShowIteration] = useState(true);
  const [pendulum, setPendulum] = useState(new Pendulum());
  const [l, setL] = useState(1.5);
  const [m, setM] = useState(1);
  const [F, setF] = useState(0);
  const [t, setT] = useState(0);
  const [odesolver, setOdesolver] = useState("rk4");
  const canvas = useRef<HTMLCanvasElement>();

  useEffect(() => {
    const canvasEle = canvas.current;
    const ctx = canvasEle.getContext("2d");

    if (!translated) ctx.translate(0, -200);
    translated = true;
  }, []);

  useEffect(() => {
    let requestId;
    const canvasEle = canvas.current;
    const ctx = canvasEle.getContext("2d");

    function draw() {
      let coords = pendulum.coordinates;

      let x1 = 0.5 * canvasEle.width;
      let y1 = 0.5 * canvasEle.height;

      ctx.fillStyle = "black";
      ctx.strokeStyle = "black";
      ctx.beginPath();
      ctx.arc(x1, y1, 5, 0, Math.PI * 2, true);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(x1, y1, 10, 0, Math.PI * 2, true);
      ctx.stroke();
      ctx.strokeStyle = "#B8B8B8";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0.5 * canvasEle.width, 0.5 * canvasEle.height + 200);
      ctx.lineTo(0.5 * canvasEle.width, 0.5 * canvasEle.height - 400);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0.5 * canvasEle.width + 185, 0.5 * canvasEle.height + 150);
      ctx.lineTo(0.5 * canvasEle.width, 0.5 * canvasEle.height);
      ctx.stroke();
      ctx.font = "bold italic 20px Arial";
      ctx.fillStyle = "#A0A0A0";
      ctx.fillText("θ", 0.5 * canvasEle.width + 15, 0.5 * canvasEle.width + 50);
      ctx.beginPath();
      ctx.moveTo(0.5 * canvasEle.width + 300, 0.5 * canvasEle.height);
      ctx.lineTo(0.5 * canvasEle.width - 300, 0.5 * canvasEle.height);
      ctx.stroke();

      for (let i = 0; i < pendulum.n; i++) {
        let x2 = 0.5 * canvasEle.width + coords[i].x * 100;
        let y2 = 0.5 * canvasEle.height + coords[i].y * 100;

        ctx.fillStyle = "red";
        ctx.strokeStyle = "black";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(x2, y2, 10 * m, 0, Math.PI * 2, true);
        ctx.fill();

        x1 = x2;
        y1 = y2;
      }
    }
    const render = () => {
      ctx.clearRect(0, 0, canvasEle.width, canvasEle.height + 200);
      draw();
      pendulum.tick(1 / 60);
      requestId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(requestId);
    };
  }, [pendulum, m]);

  useEffect(() => {
    pendulum.l = l;
    pendulum.m = m;
    pendulum.F = F;
    pendulum.t = t;
    pendulum.solver = odesolver;
    setPendulum(pendulum);
  }, [l, m, F, t, odesolver]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 7 }}>
        <Controls controls={{}} />
      </Box>
      <Box
        sx={{
          flex: 13,
          backgroundColor: "#eeeeee",
          position: "relative",
          height: "100%",
        }}
      >
        <Box sx={{ margin: 2 }}>
          <Typography style={{ textAlign: "left" }}>
            Feel free to change the parameters and see the result
          </Typography>
        </Box>
        {showIteration && (
          <Box
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
              justifyContent: "start",
              marginLeft: 2,
              marginTop: 1,
              maxWidth: 200,
            }}
          >
            <div className="control">
              <label style={{ marginRight: 24 }}>
                <Latex>length: $l$ (m)</Latex>
              </label>
              <input
                value={l}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > 0)
                    setL(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="control">
              <label style={{ marginRight: 16 }}>
                <Latex>mass: $m$ (kg)</Latex>
              </label>
              <input
                value={m}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > 0)
                    setM(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="control">
              <label style={{ marginRight: 12 }}>
                <Latex>friction: $F$ (N)</Latex>
              </label>
              <input
                value={F}
                onChange={(e) => {
                  if (parseFloat(e.target.value) >= 0)
                    setF(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <div className="control">
              <label style={{ marginRight: 20 }}>
                <Latex>torque: $\tau$ (N)</Latex>
              </label>
              <input
                value={t}
                onChange={(e) => {
                  setT(parseFloat(e.target.value));
                }}
                step={0.1}
                type="number"
              />
            </div>
            <FormControl
              fullWidth
              size="small"
              sx={{ marginTop: 2, marginBottom: 1 }}
            >
              <InputLabel id="demo-simple-select-label">ODE Solver</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={odesolver}
                label="ODE Solver"
                onChange={(e) => setOdesolver(e.target.value)}
              >
                <MenuItem value={"euler"}>Euler Method</MenuItem>
                <MenuItem value={"rk4"}>RK4</MenuItem>
              </Select>
            </FormControl>
            <Button
              sx={{ marginTop: 1 }}
              variant="outlined"
              onClick={() => {
                pendulum.thetas = Array(1).fill(0.7 * Math.PI);
                pendulum.thetaDots = Array(1).fill(0);
                setPendulum(pendulum);
              }}
            >
              Reset Position
            </Button>
          </Box>
        )}
        <canvas ref={canvas} width="700" height="700"></canvas>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function Lagrange(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of Lagrange Dynamics Equation</title>
        <meta
          name="description"
          content="Interactive Lagrange Dynamics Equation visualization: Explore a simple pendulum case by tuning mass, length, friction, and torque. See real-time results and detailed explanations of how the Lagrange equation works. Ideal for learning and visualizing dynamics in physics."
        />
      </Helmet>
    </HelmetProvider>
  );
}

export default Lagrange;
